/**
 * P3-16: Management 面板按钮——Optimize Jobs / Promote Kittens / Redeem Gift。
 *
 * 旧实现要点（js/village.js:5083-5142）：
 * - Optimize Jobs：visible=有 leader 且 canHaveLeaderOrPromote()；点击
 *   game.village.optimizeJobs()；
 * - Promote Kittens：同上 visible；点击无修饰 promoteKittens()，Shift/Ctrl/Cmd
 *   修饰 promoteKittensRepeatedly()；
 * - Redeem Gift：visible=!isEldermass() && elderBox.value>0 && !elderBox.isHidden；
 *   点击确认框后 game.redeemGift() + game.render()。
 *
 * 三者均为简单 ButtonModern（无价格/无 links），tooltip 直接返回 description。
 */
import { useCallback, useMemo } from "react";
import type { MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { t } from "../../bridge/i18n";
import { useGameTick } from "../../bridge/useGameTick";
import { GameButton } from "../../components/GameButton";
import type { TooltipContentProvider } from "../../components/Tooltip/TooltipProvider";

// ---------------------------------------------------------------------------
// 通用：visible 选择器
// ---------------------------------------------------------------------------

function useLeaderManagementVisible(): boolean {
  return useGameTick((game: GamePage | null) => {
    if (!game) {
      return false;
    }
    return Boolean(game.village.leader) && game.village.canHaveLeaderOrPromote();
  });
}

function useRedeemGiftVisible(): boolean {
  return useGameTick((game: GamePage | null) => {
    if (!game) {
      return false;
    }
    const elderBox = game.resPool.get("elderBox");
    return !game.isEldermass() && elderBox.value > 0 && !elderBox.isHidden;
  });
}

function makeDescTooltip(description: string): TooltipContentProvider {
  return () => description;
}

// ---------------------------------------------------------------------------
// Optimize Jobs
// ---------------------------------------------------------------------------

export function OptimizeJobsBtn() {
  const visible = useLeaderManagementVisible();
  const description = t("village.btn.manage.desc");
  const onClick = useCallback(() => {
    getGame()?.village.optimizeJobs();
  }, []);
  const getTooltip = useMemo(() => makeDescTooltip(description), [description]);

  return (
    <GameButton
      name={t("village.btn.manage")}
      description={description}
      visible={visible}
      getTooltip={getTooltip}
      onClick={onClick}
    />
  );
}

// ---------------------------------------------------------------------------
// Promote Kittens
// ---------------------------------------------------------------------------

export function PromoteKittensBtn() {
  const visible = useLeaderManagementVisible();
  const description = t("village.btn.promote.desc");
  const onClick = useCallback((event: MouseEvent) => {
    const game = getGame();
    if (!game) {
      return;
    }
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      game.village.promoteKittensRepeatedly();
    } else {
      game.village.promoteKittens();
    }
  }, []);
  const getTooltip = useMemo(() => makeDescTooltip(description), [description]);

  return (
    <GameButton
      name={t("village.btn.promote")}
      description={description}
      visible={visible}
      getTooltip={getTooltip}
      onClick={onClick}
    />
  );
}

// ---------------------------------------------------------------------------
// Redeem Gift
// ---------------------------------------------------------------------------

export function RedeemGiftBtn() {
  const visible = useRedeemGiftVisible();
  const onClick = useCallback(() => {
    const game = getGame();
    if (!game) {
      return;
    }
    if (game.opts.noConfirm) {
      game.redeemGift();
      game.render();
      return;
    }
    game.ui.confirm("", t("village.btn.unwrap.confirmation.msg"), () => {
      game.redeemGift();
      game.render();
    });
  }, []);

  return (
    <GameButton
      name={t("village.btn.unwrap")}
      description=""
      visible={visible}
      onClick={onClick}
    />
  );
}
